import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { retryWithBackoff as retryWithBackoffUtil } from '../utils/retry';  // Shared retry utility
import { networkService } from '../services/network';  // Shared network service

// Interface for the offline context
interface OfflineContextType {
  isOnline: boolean;
  retryWithBackoff: (fn: () => Promise<void>) => Promise<void>;
}

// Context
const OfflineContext = createContext<OfflineContextType | undefined>(undefined);

// Hook to use the offline context
export const useOffline = (): OfflineContextType => {
  const context = useContext(OfflineContext);
  if (context === undefined) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
};

// Provider component
interface OfflineProviderProps {
  children: ReactNode;
}

export const OfflineProvider: React.FC<OfflineProviderProps> = ({ children }) => {
  const [isOnline, setIsOnline] = useState<boolean>(networkService.isOnline());

  // Effect to handle network changes using shared networkService
  useEffect(() => {
    const unsubscribe = networkService.onChange((online) => {
      setIsOnline(online);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const value: OfflineContextType = {
    isOnline,
    retryWithBackoff: retryWithBackoffUtil as (fn: () => Promise<void>) => Promise<void>,
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
};